import React, {useEffect, useState} from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    Chip,
    Divider,
    LinearProgress
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Description as DescriptionIcon,
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
} from '@mui/icons-material';
import { getUserData } from '../../../../services/api/client';

const BankStatementUploadModal = ({
                                      open,
                                      onClose,
                                      propertyId,
                                      landlordId,
                                      ApiDetails,
                                      originalText
                                  }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [bankType, setBankType] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState(null);
    const [uploadResult, setUploadResult] = useState(null);

    const [bankTypes, setBankTypes] = useState([]);

    useEffect(() => {
        const fetchBankTypes = async () => {
            try {
                const userData = getUserData();
                const token = userData ? userData.find(([key]) => key === 'access_token')?.[1] : null;
                console.log("Fetched token:", token ? 'Token exists' : 'No token');

                const response = await fetch(ApiDetails + "pegasus/visionary/payments/supported-banks", {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "token": token || ""
                    }
                });

                if (!response.ok) {
                    throw new Error("Failed to fetch bank types");
                }

                const data = await response.json();
                console.log("Raw bank types response:", data);

                // Normalize response into [{ value, label }]
                let banks = [];
                if (Array.isArray(data)) {
                    banks = data.map(b => ({
                        value: b.value || b.code || b.id,   // support different field names
                        label: b.label || b.name || b.title
                    }));
                } else if (data.banks && Array.isArray(data.banks)) {
                    banks = data.banks.map(b => ({
                        value: b.value || b.code || b.id,
                        label: b.label || b.name || b.title
                    }));
                }

                setBankTypes(banks);
            } catch (error) {
                console.error("Error fetching bank types:", error);
            }
        };

        fetchBankTypes();
    }, [originalText]);


    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file type
            const allowedTypes = [
                'text/csv',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'text/plain'
            ];

            const fileExtension = file.name.toLowerCase().split('.').pop();
            const validExtensions = ['csv', 'xls', 'xlsx', 'txt'];

            if (!validExtensions.includes(fileExtension)) {
                setUploadStatus({
                    type: 'error',
                    message: 'Please upload a CSV, Excel (.xls/.xlsx), or TXT file'
                });
                return;
            }

            // Validate file size (max 10MB)
            if (file.size > 10 * 1024 * 1024) {
                setUploadStatus({
                    type: 'error',
                    message: 'File size must be less than 10MB'
                });
                return;
            }

            setSelectedFile(file);
            setUploadStatus(null);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleUpload = async () => {
        if (!selectedFile || !bankType) {
            setUploadStatus({
                type: 'error',
                message: 'Please select a file and bank type'
            });
            return;
        }

        setUploading(true);
        setUploadStatus(null);

        try {
            // Prepare form data
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('bank_type', bankType);
            formData.append('landlord_id', landlordId);

            console.log('Uploading with params:', { bankType, landlordId, fileName: selectedFile.name });

            // Make API call
            const response = await fetch(ApiDetails + "pegasus/visionary/payments/bank-statement", {
                method: "POST",
                headers: {
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                },
                body: formData
            });

            console.log('Response status:', response.status);

            if (response.ok) {
                // Check if response is Excel file
                const contentType = response.headers.get('content-type');

                if (contentType && contentType.includes('spreadsheetml')) {
                    // Handle Excel file download
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);

                    // Get filename from Content-Disposition header or create default
                    const contentDisposition = response.headers.get('content-disposition');
                    let filename = 'BankStatement_ProcessingResults.xlsx';

                    if (contentDisposition) {
                        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                        if (filenameMatch) {
                            filename = filenameMatch[1];
                        }
                    }

                    // Create download link
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);

                    setUploadStatus({
                        type: 'success',
                        message: `Bank statement processed successfully! Results downloaded as ${filename}`
                    });

                    // Set a mock result for UI display
                    setUploadResult({
                        total_transactions: 'Processing completed',
                        processed_successfully: 'Check downloaded Excel file',
                        number_of_failed_transactions: 'for detailed results',
                        number_of_unmatched_transactions: '',
                        successful_transactions: [],
                        failed_transactions: [],
                        unmatched_transactions: []
                    });
                } else {
                    // Handle JSON response (fallback or error case)
                    let result = await response.json();

                    if (typeof result === 'string') {
                        try {
                            result = JSON.parse(result);
                        } catch (parseError) {
                            console.error('Failed to parse result string:', parseError);
                        }
                    }

                    setUploadStatus({
                        type: 'success',
                        message: 'Bank statement processed successfully!'
                    });

                    const validatedResult = {
                        total_transactions: result.total_transactions || 0,
                        processed_successfully: result.processed_successfully || 0,
                        number_of_failed_transactions: result.number_of_failed_transactions || 0,
                        number_of_unmatched_transactions: result.number_of_unmatched_transactions || 0,
                        successful_transactions: result.successful_transactions || [],
                        failed_transactions: result.failed_transactions || [],
                        unmatched_transactions: result.unmatched_transactions || []
                    };

                    setUploadResult(validatedResult);
                    console.log('Final upload result set:', validatedResult);
                }
            } else {
                // Handle error response
                const errorText = await response.text();
                let errorMessage = 'Failed to process bank statement';

                try {
                    const errorJson = JSON.parse(errorText);
                    errorMessage = errorJson.Error || errorJson.error || errorMessage;
                } catch (e) {
                    errorMessage = errorText || errorMessage;
                }

                console.error('Upload failed with status:', response.status, errorMessage);
                setUploadStatus({
                    type: 'error',
                    message: errorMessage
                });
            }
        } catch (error) {
            console.error('Upload error:', error);
            setUploadStatus({
                type: 'error',
                message: 'Network error: Failed to upload bank statement'
            });
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        if (!uploading) {
            setSelectedFile(null);
            setBankType('');
            setUploadStatus(null);
            setUploadResult(null);
            onClose();
        }
    };

    const removeFile = () => {
        setSelectedFile(null);
        setUploadStatus(null);
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle
                sx={{
                    backgroundColor: '#2C5283',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CloudUploadIcon />
                    <Typography variant="h6">Upload Bank Statement</Typography>
                </Box>
                <Button
                    onClick={handleClose}
                    sx={{ color: 'white', minWidth: 'auto' }}
                    disabled={uploading}
                >
                    <CloseIcon />
                </Button>
            </DialogTitle>

            <DialogContent sx={{ padding: 3 }}>
                {uploading && (
                    <Box sx={{ mb: 2 }}>
                        <LinearProgress />
                        <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                            Processing bank statement...
                        </Typography>
                    </Box>
                )}

                {/* Upload Status */}
                {uploadStatus && (
                    <Alert
                        severity={uploadStatus.type}
                        sx={{ mb: 2 }}
                        icon={uploadStatus.type === 'success' ? <CheckCircleIcon /> : <ErrorIcon />}
                    >
                        {uploadStatus.message}
                    </Alert>
                )}

                {/* Upload Results */}
                {uploadResult && uploadStatus?.type === 'success' && (
                    <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                        <Typography variant="h6" sx={{ mb: 2, color: '#2C5283' }}>
                            Processing Results
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Chip
                                label={`Successful:  ${uploadResult.total_transactions}. ${uploadResult.processed_successfully} ${uploadResult.number_of_failed_transactions}`}
                                color="success"
                                variant="outlined"
                            />
                        </Box>
                    </Box>
                )}

                {/* Bank Type Selection */}
                <FormControl fullWidth sx={{ mb: 3, mt: 3 }}>
                    <InputLabel>Select Bank Type</InputLabel>
                    <Select
                        value={bankType}
                        label="Select Bank Type"
                        onChange={(e) => setBankType(e.target.value)}
                        disabled={uploading}
                    >
                        {bankTypes.map((bank) => (
                            <MenuItem key={bank.value} value={bank.value}>
                                {bank.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Divider sx={{ mb: 3 }} />

                {/* File Upload Area */}
                {!selectedFile ? (
                    <Box
                        sx={{
                            border: '2px dashed #ccc',
                            borderRadius: 2,
                            padding: 4,
                            textAlign: 'center',
                            backgroundColor: '#fafafa',
                            cursor: uploading ? 'not-allowed' : 'pointer',
                            opacity: uploading ? 0.6 : 1,
                            '&:hover': uploading ? {} : {
                                backgroundColor: '#f0f0f0',
                                borderColor: '#2C5283'
                            }
                        }}
                        onClick={() => !uploading && document.getElementById('file-input').click()}
                    >
                        <input
                            id="file-input"
                            type="file"
                            accept=".csv,.xls,.xlsx,.txt"
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                            disabled={uploading}
                        />
                        <CloudUploadIcon sx={{ fontSize: 48, color: '#2C5283', mb: 2 }} />
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Click to upload bank statement
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Supported formats: CSV, Excel (.xls/.xlsx), TXT
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Maximum file size: 10MB
                        </Typography>
                    </Box>
                ) : (
                    /* Selected File Display */
                    <Box
                        sx={{
                            border: '1px solid #e0e0e0',
                            borderRadius: 2,
                            padding: 2,
                            backgroundColor: '#f9f9f9'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <DescriptionIcon color="primary" />
                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                        {selectedFile.name}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {formatFileSize(selectedFile.size)}
                                    </Typography>
                                </Box>
                            </Box>
                            <Button
                                onClick={removeFile}
                                size="small"
                                color="error"
                                disabled={uploading}
                            >
                                Remove
                            </Button>
                        </Box>
                    </Box>
                )}

                {/* Instructions */}
                <Box sx={{ mt: 3, p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                        📋 Instructions:
                    </Typography>
                    <Typography variant="body2" component="div">
                        • Select the bank that issued the statement<br/>
                        • Upload the statement file (CSV or Excel format)<br/>
                        • The system will automatically match transactions to tenants<br/>
                        • SMS notifications will be sent to tenants for successful payments<br/>
                        • <strong>📊 An Excel report with detailed results will be automatically downloaded</strong>
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions sx={{ padding: 2, backgroundColor: '#f8f9fa' }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    disabled={uploading}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleUpload}
                    variant="contained"
                    disabled={!selectedFile || !bankType || uploading}
                    startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                    sx={{
                        backgroundColor: '#2C5283',
                        '&:hover': {
                            backgroundColor: '#1e3a5f'
                        }
                    }}
                >
                    {uploading ? 'Processing...' : 'Upload & Process'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BankStatementUploadModal;